import React, { useState, useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    TextField,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalculateIcon from '@mui/icons-material/Calculate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { jsPDF } from 'jspdf';
import GradientButton from './common/GradientButton';
import { useTheme } from '../theme/ThemeContext';
import { colorPalette } from '../theme/colorPalette';
import { calculateROI, validateInputs } from '../utils/calculations';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const ROICalculator = () => {
    const { mode } = useTheme();
    const colors = colorPalette[mode];
    const roiChartRef = useRef(null);
    const savingsChartRef = useRef(null);

    // Form state
    const [formData, setFormData] = useState({
        processName: '',
        processDescription: '',
        itemCount: '',
        timePerItem: '',
        userCount: '1',
        hourlyRate: '',
        automatedTime: '',
        automationCost: '',
        maintenanceCost: '0',
        riskFactor: '20',
        timeframe: '12',
    });

    const [results, setResults] = useState(null);
    const [errors, setErrors] = useState([]);
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle form submit
    const handleSubmit = (e) => {
        e.preventDefault();

        const inputs = {
            itemCount: parseFloat(formData.itemCount) || 0,
            timePerItem: parseFloat(formData.timePerItem) || 0,
            userCount: parseFloat(formData.userCount) || 1,
            hourlyRate: parseFloat(formData.hourlyRate) || 0,
            automatedTime: parseFloat(formData.automatedTime) || 0,
            automationCost: parseFloat(formData.automationCost) || 0,
            maintenanceCost: parseFloat(formData.maintenanceCost) || 0,
            timeframe: parseFloat(formData.timeframe) || 12,
            riskFactor: parseFloat(formData.riskFactor) || 20,
        };

        const validationErrors = validateInputs(inputs);
        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            setResults(null);
            return;
        }

        setErrors([]);
        const calculatedResults = calculateROI(inputs);
        setResults({ ...calculatedResults, inputs });
    };

    // Generate chart data for ROI bar chart
    const getROIChartData = () => {
        if (!results) return null;
        const { inputs } = results;
        return {
            labels: ['Implementation Cost', 'Maintenance Cost', 'Total Savings'],
            datasets: [{
                data: [
                    inputs.automationCost,
                    inputs.maintenanceCost * inputs.timeframe,
                    results.totalSavings
                ],
                backgroundColor: [
                    'rgba(255, 99, 132, 0.5)',
                    'rgba(255, 159, 64, 0.5)',
                    'rgba(75, 192, 192, 0.5)'
                ],
                borderColor: [
                    'rgb(255, 99, 132)',
                    'rgb(255, 159, 64)',
                    'rgb(75, 192, 192)'
                ],
                borderWidth: 1
            }]
        };
    };

    // Generate chart data for savings line chart
    const getSavingsChartData = () => {
        if (!results) return null;
        const { inputs } = results;
        const monthlyData = Array.from({ length: inputs.timeframe }, (_, i) => {
            const month = i + 1;
            const totalCost = inputs.automationCost + (inputs.maintenanceCost * month);
            const cumulativeSavings = results.grossMonthlySavings * month;
            return {
                cost: totalCost,
                savings: cumulativeSavings,
                net: cumulativeSavings - totalCost
            };
        });

        return {
            labels: monthlyData.map((_, i) => `Month ${i + 1}`),
            datasets: [
                {
                    label: 'Total Cost',
                    data: monthlyData.map(d => d.cost),
                    borderColor: 'rgb(255, 99, 132)',
                    backgroundColor: 'rgba(255, 99, 132, 0.1)',
                    fill: true
                },
                {
                    label: 'Cumulative Gross Savings',
                    data: monthlyData.map(d => d.savings),
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.1)',
                    fill: true
                },
                {
                    label: 'Net Benefit',
                    data: monthlyData.map(d => d.net),
                    borderColor: 'rgb(54, 162, 235)',
                    backgroundColor: 'rgba(54, 162, 235, 0.1)',
                    fill: true
                }
            ]
        };
    };

    const roiChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: 'Cost vs. Savings Analysis' }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => '£' + value.toLocaleString()
                }
            }
        }
    };

    const savingsChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: 'Savings Over Time' }
        },
        scales: {
            y: {
                ticks: {
                    callback: (value) => '£' + value.toLocaleString()
                }
            }
        }
    };

    // Export to PDF
    const handleExportPDF = async () => {
        if (!results) {
            alert('Please calculate ROI first before exporting.');
            return;
        }

        try {
            const doc = new jsPDF();
            const { inputs } = results;

            // Using Helvetica - clean, professional font with proper bold support
            const fontFamily = 'helvetica';

            // Colour palette - minimalist
            const primaryColor = [45, 52, 54]; // Dark grey
            const accentColor = [9, 132, 227]; // Blue accent - use sparingly
            const lightGrey = [245, 245, 245];
            const textDark = [45, 52, 54];
            const textMuted = [120, 120, 120];

            // Helper function for section headers (minimalist)
            const drawSectionHeader = (title, yPos) => {
                doc.setFontSize(14);
                doc.setTextColor(...primaryColor);
                doc.setFont(fontFamily, 'bold');
                doc.text(title, 20, yPos);
                // Subtle underline
                doc.setDrawColor(...accentColor);
                doc.setLineWidth(0.5);
                doc.line(20, yPos + 2, 190, yPos + 2);
                doc.setFont(fontFamily, 'normal');
                return yPos + 10;
            };

            // Helper for key-value pairs
            const drawKeyValue = (key, value, yPos, isBold = false) => {
                doc.setFontSize(10);
                doc.setTextColor(...textMuted);
                doc.text(key, 20, yPos);
                doc.setTextColor(...textDark);
                if (isBold) doc.setFont(fontFamily, 'bold');
                doc.text(value, 80, yPos);
                doc.setFont(fontFamily, 'normal');
                return yPos + 6;
            };

            // === PAGE 1: Executive Summary ===

            // Header - clean, minimal
            doc.setFillColor(...primaryColor);
            doc.rect(0, 0, 210, 40, 'F');

            // Title
            doc.setFontSize(22);
            doc.setTextColor(255, 255, 255);
            doc.setFont(fontFamily, 'bold');
            doc.text('Automation ROI Report', 20, 22);

            // Process name as subtitle
            doc.setFontSize(12);
            doc.setFont(fontFamily, 'normal');
            doc.text(formData.processName || 'Untitled Process', 20, 32);

            // Date in top right
            doc.setFontSize(10);
            const dateStr = new Date().toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric'
            });
            doc.text(dateStr, 190, 32, { align: 'right' });

            let y = 55;

            // Key Metrics Box - highlighted with accent
            doc.setFillColor(...lightGrey);
            doc.roundedRect(15, y - 5, 180, 45, 3, 3, 'F');

            // Accent stripe on left
            doc.setFillColor(...accentColor);
            doc.rect(15, y - 5, 4, 45, 'F');

            doc.setFontSize(11);
            doc.setFont(fontFamily, 'bold');
            doc.setTextColor(...primaryColor);
            doc.text('Key Metrics', 25, y + 5);

            y += 12;
            doc.setFont(fontFamily, 'normal');
            doc.setFontSize(10);

            // Two-column layout for metrics
            doc.setTextColor(...textMuted);
            doc.text('Time Saved:', 25, y);
            doc.text('Monthly Net Savings:', 110, y);
            doc.setTextColor(...primaryColor);
            doc.setFont(fontFamily, 'bold');
            doc.text(`${(results.netTimeSavedPerMonth * inputs.userCount).toFixed(1)} hrs/month`, 60, y);
            doc.text(`£${results.monthlySavings.toFixed(2)}`, 160, y);

            y += 8;
            doc.setFont(fontFamily, 'normal');
            doc.setTextColor(...textMuted);
            doc.text('Break-Even:', 25, y);
            doc.text('Risk-Adjusted ROI:', 110, y);
            doc.setTextColor(...primaryColor);
            doc.setFont(fontFamily, 'bold');
            doc.text(results.breakEvenMonths === Infinity ? 'N/A' : `${results.breakEvenMonths.toFixed(1)} months`, 60, y);
            doc.setTextColor(...accentColor); // Accent for the main ROI figure
            doc.text(`${results.adjustedROI.toFixed(1)}%`, 160, y);

            y += 8;
            doc.setFont(fontFamily, 'normal');
            doc.setTextColor(...textMuted);
            doc.text('Gross ROI:', 25, y);
            doc.text('Total Savings:', 110, y);
            doc.setTextColor(...primaryColor);
            doc.setFont(fontFamily, 'bold');
            doc.text(`${results.roi.toFixed(1)}%`, 60, y);
            doc.text(`£${results.totalSavings.toFixed(2)}`, 160, y);

            y += 20;

            // Process Description (if exists)
            if (formData.processDescription) {
                y = drawSectionHeader('Process Description', y);
                doc.setFontSize(10);
                doc.setTextColor(...textDark);
                const descLines = doc.splitTextToSize(formData.processDescription, 170);
                doc.text(descLines, 20, y);
                y += descLines.length * 5 + 10;
            }

            // Financial Summary
            y = drawSectionHeader('Financial Summary', y);
            y = drawKeyValue('Gross Monthly Savings:', `£${results.grossMonthlySavings.toFixed(2)}`, y);
            y = drawKeyValue('Monthly Maintenance:', `£${inputs.maintenanceCost.toFixed(2)}`, y);
            y = drawKeyValue('Net Monthly Savings:', `£${results.monthlySavings.toFixed(2)}`, y, true);
            y += 4;
            y = drawKeyValue('Total Investment:', `£${results.totalCost.toFixed(2)}`, y);
            y = drawKeyValue('Total Gross Savings:', `£${results.totalSavings.toFixed(2)}`, y);
            y = drawKeyValue('Net Return:', `£${(results.totalSavings - results.totalCost).toFixed(2)}`, y, true);

            y += 10;

            // Charts
            if (roiChartRef.current && savingsChartRef.current) {
                y = drawSectionHeader('Visual Analysis', y);

                const roiCanvas = roiChartRef.current.canvas;
                const savingsCanvas = savingsChartRef.current.canvas;

                if (roiCanvas && savingsCanvas) {
                    const chartWidth = 85;
                    const chartHeight = 55;
                    doc.addImage(roiCanvas.toDataURL('image/png'), 'PNG', 15, y, chartWidth, chartHeight);
                    doc.addImage(savingsCanvas.toDataURL('image/png'), 'PNG', 105, y, chartWidth, chartHeight);
                    y += chartHeight + 5;

                    // Chart labels
                    doc.setFontSize(8);
                    doc.setTextColor(...textMuted);
                    doc.text('Cost vs Savings', 57.5, y, { align: 'center' });
                    doc.text('Cumulative Over Time', 147.5, y, { align: 'center' });
                }
            }

            // === PAGE 2: Detailed Analysis ===
            doc.addPage();

            // Header - lighter for page 2
            doc.setFillColor(...lightGrey);
            doc.rect(0, 0, 210, 25, 'F');
            doc.setFontSize(16);
            doc.setTextColor(...primaryColor);
            doc.setFont(fontFamily, 'bold');
            doc.text('Detailed Calculations', 20, 17);
            doc.setFont(fontFamily, 'normal');
            doc.setFontSize(10);
            doc.setTextColor(...textMuted);
            doc.text(formData.processName || 'Untitled Process', 190, 17, { align: 'right' });

            y = 40;

            // Input Parameters
            y = drawSectionHeader('Input Parameters', y);
            y = drawKeyValue('Items Processed:', `${inputs.itemCount}/month`, y);
            y = drawKeyValue('Manual Time/Item:', `${inputs.timePerItem} minutes`, y);
            y = drawKeyValue('Automated Time/Item:', `${inputs.automatedTime} minutes`, y);
            y = drawKeyValue('Number of Users:', `${inputs.userCount}`, y);
            y = drawKeyValue('Hourly Rate:', `£${inputs.hourlyRate}`, y);
            y = drawKeyValue('Automation Cost:', `£${inputs.automationCost}`, y);
            y = drawKeyValue('Monthly Maintenance:', `£${inputs.maintenanceCost}`, y);
            y = drawKeyValue('Analysis Period:', `${inputs.timeframe} months`, y);
            y = drawKeyValue('Risk Factor:', `${inputs.riskFactor}%`, y);

            y += 10;

            // Calculations
            y = drawSectionHeader('Calculation Breakdown', y);
            doc.setFontSize(9);
            doc.setTextColor(...textDark);

            const calculations = [
                `1. Total Manual Time: ${inputs.itemCount} × ${inputs.timePerItem} = ${results.totalManualMinutesPerMonth.toFixed(0)} mins/month`,
                `2. Total Automated Time: ${inputs.itemCount} × ${inputs.automatedTime} = ${results.totalAutomatedMinutesPerMonth.toFixed(0)} mins/month`,
                `3. Time Saved per User: ${results.netTimeSavedPerMonth.toFixed(1)} hours/month`,
                `4. Total Team Time Saved: ${results.netTimeSavedPerMonth.toFixed(1)} × ${inputs.userCount} = ${(results.netTimeSavedPerMonth * inputs.userCount).toFixed(1)} hours/month`,
                `5. Gross Monthly Savings: ${(results.netTimeSavedPerMonth * inputs.userCount).toFixed(1)} hrs × £${inputs.hourlyRate} = £${results.grossMonthlySavings.toFixed(2)}`,
                `6. Net Monthly Savings: £${results.grossMonthlySavings.toFixed(2)} - £${inputs.maintenanceCost} = £${results.monthlySavings.toFixed(2)}`,
                `7. Total Investment: £${inputs.automationCost} + (£${inputs.maintenanceCost} × ${inputs.timeframe}) = £${results.totalCost.toFixed(2)}`,
                `8. Total Savings: £${results.grossMonthlySavings.toFixed(2)} × ${inputs.timeframe} = £${results.totalSavings.toFixed(2)}`,
                `9. ROI: ((£${results.totalSavings.toFixed(2)} - £${results.totalCost.toFixed(2)}) ÷ £${inputs.automationCost}) × 100 = ${results.roi.toFixed(1)}%`,
                `10. Risk-Adjusted ROI: ${results.roi.toFixed(1)}% × ${((100 - inputs.riskFactor) / 100).toFixed(2)} = ${results.adjustedROI.toFixed(1)}%`
            ];

            calculations.forEach(calc => {
                doc.text(calc, 20, y);
                y += 5;
            });

            y += 10;

            // Sensitivity Analysis
            y = drawSectionHeader('Sensitivity Analysis', y);
            doc.setFontSize(10);

            // Table header
            doc.setFillColor(...lightGrey);
            doc.rect(20, y - 3, 170, 8, 'F');
            doc.setTextColor(...primaryColor);
            doc.setFont(fontFamily, 'bold');
            doc.text('Scenario', 25, y + 2);
            doc.text('ROI', 100, y + 2);
            doc.text('Break-Even', 140, y + 2);
            y += 10;

            doc.setFont(fontFamily, 'normal');
            const scenarios = [
                { name: 'Conservative (70%)', data: results.sensitivityAnalysis.conservative },
                { name: 'Moderate (Baseline)', data: results.sensitivityAnalysis.moderate },
                { name: 'Optimistic (130%)', data: results.sensitivityAnalysis.optimistic }
            ];

            scenarios.forEach(scenario => {
                doc.setTextColor(...textDark);
                doc.text(scenario.name, 25, y);
                doc.text(`${scenario.data.roi.toFixed(1)}%`, 100, y);
                doc.text(scenario.data.breakEven === Infinity ? 'N/A' : `${scenario.data.breakEven.toFixed(1)} months`, 140, y);
                y += 6;
            });

            // Footer with disclaimer (both pages)
            [1, 2].forEach(pageNum => {
                doc.setPage(pageNum);
                doc.setFontSize(7);
                doc.setTextColor(...textMuted);
                doc.text(
                    'Disclaimer: This report is for informational purposes only and does not constitute financial advice. Results are estimates based on provided inputs.',
                    105, 287, { align: 'center', maxWidth: 180 }
                );
                doc.text(`Page ${pageNum} of 2`, 190, 287, { align: 'right' });
            });

            // Save PDF with clean filename: "Process Name - YYYY-MM-DD.pdf"
            const dateString = new Date().toISOString().split('T')[0];
            const cleanProcessName = (formData.processName || 'ROI Report')
                .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename chars
                .trim();
            doc.save(`${cleanProcessName} - ${dateString}.pdf`);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please check console for details.');
        }
    };

    // Styles
    const cardStyle = {
        borderRadius: '20px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(5px)',
        border: `1px solid ${colors.divider}`,
        background: mode === 'light'
            ? 'rgba(255, 255, 255, 0.7)'
            : 'rgba(45, 52, 54, 0.7)',
    };

    const sectionTitleStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 3,
        color: colors.secondary.main,
        fontWeight: 600,
    };

    const resultCardStyle = {
        ...cardStyle,
        textAlign: 'center',
        height: '100%',
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 }, position: 'relative', zIndex: 1 }}>
            {/* Back to Tools Button */}
            <Box sx={{ mb: 4 }}>
                <GradientButton
                    variant="filled"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => window.location.href = 'https://rastojaniglos.co.uk/tools'}
                >
                    Back to Tools
                </GradientButton>
            </Box>

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                            background: colors.background.gradient.primary,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Automation ROI Calculator
                    </Typography>
                    <GradientButton
                        variant="primary"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHowItWorksOpen(true)}
                    >
                        How it Works
                    </GradientButton>
                </Box>
            </motion.div>

            {/* Main Form Card */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
            >
                <Paper sx={{ ...cardStyle, p: { xs: 2, sm: 3, md: 4 } }}>
                    <form onSubmit={handleSubmit}>
                        {/* Process Details Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={sectionTitleStyle}>
                                <SettingsIcon /> Process Details
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Process Name"
                                        name="processName"
                                        value={formData.processName}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Process Description"
                                        name="processDescription"
                                        value={formData.processDescription}
                                        onChange={handleChange}
                                        multiline
                                        rows={3}
                                        inputProps={{ maxLength: 300 }}
                                        helperText={`${formData.processDescription.split(/\s+/).filter(Boolean).length}/50 words`}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Number of Items to Automate (monthly)"
                                        name="itemCount"
                                        type="number"
                                        value={formData.itemCount}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Manual Time per Item (minutes)"
                                        name="timePerItem"
                                        type="number"
                                        value={formData.timePerItem}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Resource Details Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={sectionTitleStyle}>
                                <PeopleIcon /> Resource Details
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Number of Users"
                                        name="userCount"
                                        type="number"
                                        value={formData.userCount}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Hourly Rate (£)"
                                        name="hourlyRate"
                                        type="number"
                                        value={formData.hourlyRate}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Automation Details Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={sectionTitleStyle}>
                                <SmartToyIcon /> Automation Details
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Automated Time per Item (minutes)"
                                        name="automatedTime"
                                        type="number"
                                        value={formData.automatedTime}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Automation Cost (£)"
                                        name="automationCost"
                                        type="number"
                                        value={formData.automationCost}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Monthly Maintenance Cost (£)"
                                        name="maintenanceCost"
                                        type="number"
                                        value={formData.maintenanceCost}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Risk Factor (%)"
                                        name="riskFactor"
                                        type="number"
                                        value={formData.riskFactor}
                                        onChange={handleChange}
                                        helperText="Higher risk factor means lower confidence in results"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Calculate ROI Over (months)"
                                        name="timeframe"
                                        type="number"
                                        value={formData.timeframe}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Error Messages */}
                        {errors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {errors.map((error, index) => (
                                    <div key={index}>{error}</div>
                                ))}
                            </Alert>
                        )}

                        {/* Submit Button */}
                        <GradientButton
                            variant="filled"
                            type="submit"
                            fullWidth
                            startIcon={<CalculateIcon />}
                        >
                            Calculate ROI
                        </GradientButton>
                    </form>

                    {/* Results Section */}
                    {results && (
                        <Box sx={{ mt: 6 }}>
                            <Typography variant="h4" sx={{ textAlign: 'center', mb: 4, color: colors.text.primary }}>
                                Results Summary
                            </Typography>

                            {/* Results Cards */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={6} md={3}>
                                    <Card sx={resultCardStyle}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Time Saved
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: colors.secondary.main, fontWeight: 700 }}>
                                                {results.netTimeSavedPerMonth.toFixed(1)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                hours/month
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Card sx={resultCardStyle}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Monthly Savings
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: colors.secondary.main, fontWeight: 700 }}>
                                                £{results.monthlySavings.toFixed(0)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                per month
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Card sx={resultCardStyle}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Break Even
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: colors.secondary.main, fontWeight: 700 }}>
                                                {results.breakEvenMonths === Infinity ? '∞' : results.breakEvenMonths.toFixed(1)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                months
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Card sx={resultCardStyle}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Risk-Adjusted ROI
                                            </Typography>
                                            <Typography variant="h4" sx={{ color: colors.secondary.main, fontWeight: 700 }}>
                                                {results.adjustedROI.toFixed(0)}%
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ({results.roi.toFixed(0)}% gross)
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Charts Row */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={cardStyle}>
                                        <CardContent sx={{ height: 300 }}>
                                            <Bar
                                                ref={roiChartRef}
                                                data={getROIChartData()}
                                                options={roiChartOptions}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Card sx={cardStyle}>
                                        <CardContent sx={{ height: 300 }}>
                                            <Line
                                                ref={savingsChartRef}
                                                data={getSavingsChartData()}
                                                options={savingsChartOptions}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Export Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                                <GradientButton
                                    variant="filled"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleExportPDF}
                                >
                                    Export PDF
                                </GradientButton>
                            </Box>

                            {/* Detailed Calculations Accordion */}
                            <Accordion sx={cardStyle}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography fontWeight={600}>Time Savings Calculation</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary">
                                        Manual time per item: {results.inputs.timePerItem} minutes<br />
                                        Automated time per item: {results.inputs.automatedTime} minutes<br />
                                        Items processed: {results.inputs.itemCount}/month<br /><br />
                                        Total manual time: {results.totalManualMinutesPerMonth.toFixed(0)} minutes/month<br />
                                        Total automated time: {results.totalAutomatedMinutesPerMonth.toFixed(0)} minutes/month<br />
                                        Net time saved per user: {results.netTimeSavedPerMonth.toFixed(1)} hours/month<br /><br />
                                        <strong>Number of users: {results.inputs.userCount}</strong><br />
                                        <strong>Total team time saved: {(results.netTimeSavedPerMonth * results.inputs.userCount).toFixed(1)} hours/month</strong>
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion sx={cardStyle}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography fontWeight={600}>Financial Analysis</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Monthly Figures:</strong><br />
                                        Gross monthly savings: £{results.grossMonthlySavings.toFixed(2)}<br />
                                        Maintenance cost: £{results.inputs.maintenanceCost.toFixed(2)}<br />
                                        Net monthly savings: £{results.monthlySavings.toFixed(2)}<br /><br />
                                        <strong>Period Totals ({results.inputs.timeframe} months):</strong><br />
                                        Total gross savings: £{results.totalSavings.toFixed(2)}<br />
                                        Total maintenance cost: £{(results.inputs.maintenanceCost * results.inputs.timeframe).toFixed(2)}<br />
                                        Total automation cost: £{results.inputs.automationCost.toFixed(2)}<br />
                                        Total cost (automation + maintenance): £{results.totalCost.toFixed(2)}<br /><br />
                                        <strong>Annualised:</strong><br />
                                        Annual net savings: £{results.annualSavings.toFixed(2)}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion sx={cardStyle}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography fontWeight={600}>Sensitivity Analysis</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary" component="div">
                                        <strong>ROI Projections (accounting for uncertainty):</strong><br /><br />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <span>🔴 Conservative (70%):</span>
                                            <span><strong>{results.sensitivityAnalysis.conservative.roi.toFixed(1)}% ROI</strong> | {results.sensitivityAnalysis.conservative.breakEven === Infinity ? 'N/A' : results.sensitivityAnalysis.conservative.breakEven.toFixed(1) + ' months'} break-even</span>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <span>🟡 Moderate (baseline):</span>
                                            <span><strong>{results.sensitivityAnalysis.moderate.roi.toFixed(1)}% ROI</strong> | {results.sensitivityAnalysis.moderate.breakEven === Infinity ? 'N/A' : results.sensitivityAnalysis.moderate.breakEven.toFixed(1) + ' months'} break-even</span>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>🟢 Optimistic (130%):</span>
                                            <span><strong>{results.sensitivityAnalysis.optimistic.roi.toFixed(1)}% ROI</strong> | {results.sensitivityAnalysis.optimistic.breakEven === Infinity ? 'N/A' : results.sensitivityAnalysis.optimistic.breakEven.toFixed(1) + ' months'} break-even</span>
                                        </Box>
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>

                            <Accordion sx={cardStyle}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography fontWeight={600}>ROI Calculation</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary" component="div">
                                        <strong>1. Total Cost:</strong><br />
                                        £{results.inputs.automationCost} implementation + (£{results.inputs.maintenanceCost} × {results.inputs.timeframe} months) = £{results.totalCost.toFixed(2)}<br /><br />
                                        <strong>2. Gross Monthly Savings:</strong><br />
                                        {results.netTimeSavedPerMonth.toFixed(1)} hours × £{results.inputs.hourlyRate} × {results.inputs.userCount} users = £{results.grossMonthlySavings.toFixed(2)}<br /><br />
                                        <strong>3. Total Savings (over {results.inputs.timeframe} months):</strong><br />
                                        £{results.grossMonthlySavings.toFixed(2)} × {results.inputs.timeframe} = £{results.totalSavings.toFixed(2)}<br /><br />
                                        <strong>4. ROI Formula:</strong><br />
                                        ((Total Savings - Total Cost) ÷ Automation Cost) × 100<br />
                                        ((£{results.totalSavings.toFixed(2)} - £{results.totalCost.toFixed(2)}) ÷ £{results.inputs.automationCost}) × 100 = {results.roi.toFixed(1)}%<br /><br />
                                        <strong>5. Risk-Adjusted ROI:</strong><br />
                                        {results.roi.toFixed(1)}% × {((100 - results.inputs.riskFactor) / 100).toFixed(2)} = {results.adjustedROI.toFixed(1)}%<br /><br />
                                        <strong>6. Break-Even Point:</strong><br />
                                        £{results.inputs.automationCost} ÷ £{results.monthlySavings.toFixed(2)} (net monthly savings) = {results.breakEvenMonths === Infinity ? 'N/A' : results.breakEvenMonths.toFixed(1) + ' months'}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* How it Works Modal */}
            <Dialog
                open={howItWorksOpen}
                onClose={() => setHowItWorksOpen(false)}
                maxWidth="md"
                PaperProps={{ sx: cardStyle }}
            >
                <DialogTitle>
                    How to Use the ROI Calculator
                </DialogTitle>
                <DialogContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Steps to Calculate ROI:
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemText primary="1. Enter the process details including name and description" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="2. Input the number of items processed monthly" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="3. Specify the time taken per item in minutes" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="4. Enter the hourly rate for the process" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary="5. Input the automation cost and expected automated time per item" />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary='6. Click "Calculate ROI" to see the results' />
                        </ListItem>
                        <ListItem>
                            <ListItemText primary='7. Use "Export to PDF" to save or share the results' />
                        </ListItem>
                    </List>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600}>Legal Disclaimer</Typography>
                        <Typography variant="body2">
                            This calculator is provided "as is" without any warranties. The calculations do not constitute financial or investment advice. Your use of this calculator is entirely at your own risk.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHowItWorksOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ROICalculator;
